/**
 * まとめて送った承認 / 却下の結末。案件ごとの応答を 1 つに畳む。
 *
 * 画面（`ApprovalsPage`）から切り出しているのは、応答の畳み込みが描画と独立に検証できる
 * 純粋な計算だからである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件ごとの承認の応答を、積めた件数と共通部が拒否した理由に畳む。
 * @graph-connects none
 */
import type { ApproveTermChangesOutput } from '@cortex/domain-spec-console';

/**
 * まとめて送った判断の結末。案件ごとの応答を 1 つに畳む。
 *
 * 送信そのものが失敗した（`failed`）ときは、どの案件まで積めたかが応答から分からないので
 * 件数を出さない。一覧は取り直されるので、積めた行は状態の変化で読める。
 * @graph-connects none
 */
export type BatchOutcome =
  | { readonly kind: 'done'; readonly applied: number; readonly rejections: readonly string[] }
  | { readonly kind: 'failed'; readonly message: string };

/**
 * 案件ごとの承認の応答を 1 つの結末に畳む。共通部の拒否は例外ではなく値で返るので、ここで
 * 件数と理由に分ける。
 *
 * @param results 案件ごとの応答
 * @returns 積めた件数と、共通部が拒否した理由の文
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件ごとの承認の応答を、積めた件数と拒否の理由に畳む。
 * @graph-connects none
 */
export function summarizeOutcomes(results: readonly ApproveTermChangesOutput[]): BatchOutcome {
  return {
    kind: 'done',
    applied: results.reduce(
      (total, result) => total + (result.outcome === 'applied' ? result.decidedNodeIds.length : 0),
      0,
    ),
    rejections: results.flatMap((result) =>
      result.outcome === 'rejected'
        ? result.rejections.map((rejection) => `${String(result.pjNumber)}: ${rejection.message}`)
        : [],
    ),
  };
}
